import { Fragment, useState, useEffect } from "react";
import { bulkGenerateAndDownloadQR } from "../../components/shared/bulk-qr-generator";

import { Button, MediaQuery, Modal, Table } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useSearchParams } from "react-router-dom";
import { Tank, FuelType } from "@captiva/shared-types";
import { TanksAssetConfig } from "../../components/listings/configurations/assets/tank-asset-config";
import { AssetEditor } from "../../components/shared/asset-editor";
import { FlexOptionsChoiceModal } from "./shared/flex-options-choice-modal";
import { Plus } from "tabler-icons-react";
import { useCreateFlexOptionsSet } from "../../hooks/useCreateFlexOptionsSet";
import TableRowActions from "./shared/table-row-actions";
import { BulkActionsMenu } from "./shared/bulk-actions-menu";

export interface AuthUser {
  uid: string;
  email?: string;
  claims?: {
    companyId?: string;
  };
}

interface TankTableProps {
  elements: Tank[];
  auth: AuthUser | null;
  reloadCallback: () => void;
  fuelTypes?: FuelType[];
}

export function TankTable({ elements, auth, reloadCallback, fuelTypes = [] }: TankTableProps) {
  const newTank: Partial<Tank> = {
    name: "",
    size: "",
    mobileTank: false,
    isActive: false,
    fuelReceiveBulkDelivery: false,
    fuelTypeId: undefined,
  };
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [searchParams, setSearchParams] = useSearchParams();
  const [editedElement, setEditedElement] = useState<Tank | Partial<Tank> | undefined>(undefined);

  // Auto-open asset editor if editId is in query params
  useEffect(() => {
    const editId = searchParams.get('editId');
    if (editId) {
      const match = elements.find((el) => el.id === editId);
      if (match) {
        setEditedElement(match);
      }
    }
  }, [searchParams, elements]);

  const closeEdit = () => {
    setEditedElement(undefined);
    if (searchParams.get('editId')) {
      searchParams.delete('editId');
      setSearchParams(searchParams, { replace: true });
    }
  };
  const [createFlexOptionsSet, { loading, notification, clearNotification, existingDocs, mergeOptions, createNewSet }] = useCreateFlexOptionsSet();
  
  const handleCreateFlexOptionsSet = () => {
    const companyId = auth?.claims?.companyId;
    if (!companyId) {
      alert("No company ID found in auth claims.");
      return;
    }
    createFlexOptionsSet({
      assetType: "Tanks",
      assetList: elements,
      metadataPrefix: "tank",
      fieldMappings: {
        label: "name",
        value: "id",
        metadata: {
          size: "size",
          fuelType: "fuelType",
          mobileTank: "mobileTank",
          fuelReceiveBulkDelivery: "fuelReceiveBulkDelivery",
          name: "name",
        }
      },
      companyId
    });
  };

  const rows = elements.map((element) => (
    <Fragment key={element.id}>
      {/* // desktop sizes */}
      <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.name}</td>
          <td>{element.size}</td>
          <td>{String(element.isActive ?? true)}</td>
          <td>{element.fuelType}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="Tank"
              collectionName="tanks"
              nameKey="name"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
      {/* // phone sizes */}
      <MediaQuery largerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.name}</td>
          <td>{element.size}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedElement}
              reloadCallback={reloadCallback}
              assetName="Tank"
              collectionName="tanks"
              nameKey="name"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
    </Fragment>
  ));

  async function handleBulkQRDownload() {
    await bulkGenerateAndDownloadQR({
      assetName: "Tank",
      items: elements,
      nameKey: "name",
      idKey: "id",
      zipName: "tanks-qr-codes.zip",
      logoSrc: "/captiva-mark.png"
    });
  }
  return (
    <>
      {notification && notification.type !== "choice" && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={clearNotification}>
            {notification.message}
          </Button>
        </div>
      )}
      {elements.length > 0 && (
        <Table width="100%">
          <thead>
            <tr>
              <th>Tank name</th>
              <th>Size (litres)</th>
              <th>Active</th>
              <th>Fuel type</th>
              <th colSpan={7} style={{textAlign: 'left'}}>
                <BulkActionsMenu onBulkQRDownload={handleBulkQRDownload} onCreateFlexOptionsSet={handleCreateFlexOptionsSet} loading={loading} />
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      )}
      {editedElement && (
        <Modal opened onClose={closeEdit} title="Tank" {...({ fullScreen: isMobile } as any)}>
          <AssetEditor
            asset={editedElement}
            assetConfig={TanksAssetConfig}
            collection="tanks"
            auth={auth}
            reloadCallback={() => {
              reloadCallback();
              closeEdit();
            }}
            dependencyList={{
              fuelTypeId: fuelTypes.map(ft => ({ id: ft.id, label: ft.name }))
            }}
            dependencyLabel={{ fuelTypeId: "label" }}
          ></AssetEditor>
        </Modal>
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button
          onClick={() => setEditedElement(newTank)}
          fullWidth
          style={{ maxWidth: 500 }}
          my="xl"
          color="cap-navy"
          variant="outline"
          leftIcon={<Plus />}
        >
          Create new
        </Button>
      </div>
      {notification && notification.type === "choice" && (
        <FlexOptionsChoiceModal
          opened={true}
          onClose={() => clearNotification()}
          onMerge={mergeOptions}
          onCreateNew={createNewSet}
          existingDocs={existingDocs}
          assetType="Tanks"
        />
      )}
    </>
  );
}
